import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useRef, useState } from 'react';
import { drainSse, toDelta, StreamError } from 'src/lib/livyStream';
import { livyJson } from 'src/lib/livyApi';
import { livyFetch } from 'src/lib/livyTransport';
import { markLivyRevoked } from './access';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
/** The company the app has selected, as the header shows it; undefined when none is. */
export const selectedCompanyName = () => {
  const name = localStorage.getItem(USER_SETTINGS_KEYS.companyName);
  return name && name !== 'Select account' ? name : undefined;
};

/** Page context the drawer attaches so "why is this wrong?" resolves without the user naming anything. */

/** The resident a staff resident preview runs as; the proxy and MCP service check it belongs to the company. */

/**
 * All Livy chat state. Both shells, the full page and the drawer, are thin consumers of this,
 * which is why page-vs-drawer is one engine rather than two implementations.
 */
/** What a refused turn means to the person, from the proxy's {error} code. */
export async function refusal(res) {
  const code = (await res.json().catch(() => ({}))).error;
  if (res.status === 403 && code === 'not_enabled') {
    markLivyRevoked();
    return "Livy isn't available on your account yet. Your Livable account manager can turn it on.";
  }
  if (res.status === 401) return 'Your session expired. Sign in again.';
  if (res.status === 413) return 'This conversation is too long to continue. Start a new one.';
  if (res.status === 503 && code === 'livy_disabled') return 'Livy is paused right now. Try again later.';
  return 'Livy is unavailable right now. Try again in a moment.';
}

/** How often the panel checks for a rep's lines while a person is asked for or in the chat. */
export const LIVE_POLL_MS = 4000;
export const isLiveActive = live => (live === null || live === void 0 ? void 0 : live.state) === 'waiting' || (live === null || live === void 0 ? void 0 : live.state) === 'joined';

/**
 * The history the proxy reads for a staff view (a customer's comes from the server): events are panel-only, and a
 * rep's line goes in as an assistant turn that says who wrote it, as the proxy's forModel does.
 */
export const toApiHistory = messages => messages.filter(m => m.role !== 'event').map(m => m.role === 'rep' ? {
  role: 'assistant',
  content: "(".concat(m.name || 'Livable support', ", from Livable's support team, wrote:) ").concat(m.content)
} : {
  role: m.role,
  content: m.content
});
export function useLivyChat(_ref) {
  let config = _ref.config,
    getToken = _ref.getToken,
    context = _ref.context,
    _ref$surface = _ref.surface,
    surface = _ref$surface === void 0 ? 'admin' : _ref$surface,
    billingType = _ref.billingType,
    resident = _ref.resident;
  const _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    messages = _useState2[0],
    setMessages = _useState2[1];
  const _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    live = _useState4[0],
    setLive = _useState4[1];
  // One id per conversation ties every logged turn, tool call and transcript row together.
  const _useState5 = useState(() => crypto.randomUUID()),
    _useState6 = _slicedToArray(_useState5, 2),
    conversationId = _useState6[0],
    setConversationId = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    streaming = _useState8[0],
    setStreaming = _useState8[1];
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    error = _useState0[0],
    setError = _useState0[1];
  const abortRef = useRef(null);
  const stop = useCallback(() => {
    var _abortRef$current;
    return (_abortRef$current = abortRef.current) === null || _abortRef$current === void 0 ? void 0 : _abortRef$current.abort();
  }, []);

  // A turn outlives the component that started it unless someone aborts it. Closing the drawer or
  // switching surface mid-stream left the fetch running and its reader still calling setState, so
  // the tokens of an answer nobody can see were still being parsed and applied. Keyed on surface
  // as well as unmount: the surface is part of the request, so a stream started under the old one
  // is answering a prompt the user has already navigated away from.
  useEffect(() => () => {
    var _abortRef$current2;
    (_abortRef$current2 = abortRef.current) === null || _abortRef$current2 === void 0 || _abortRef$current2.abort();
    abortRef.current = null;
  }, [surface]);
  const send = useCallback(
  // `base` lets retry rebuild from the truncated history. Without it the send closed over the
  // pre-update messages array, so a retry re-sent the answer it was meant to replace.
  // Resolves with the answer's text once it is whole; null when the send was dropped, stopped or failed.
  async (text, base, opts) => {
    const trimmed = text.trim();
    if (!trimmed || streaming) return null;
    setError(null);
    setStreaming(true);
    const channel = opts !== null && opts !== void 0 && opts.voice ? 'voice' : undefined;
    const history = [...(base !== null && base !== void 0 ? base : messages), {
      role: 'user',
      content: trimmed,
      channel
    }];
    setMessages([...history, {
      role: 'assistant',
      content: '',
      tools: [],
      requestIds: [],
      channel
    }]);
    // An object, like `seen` below: the SSE callback runs inside the read loop.
    const answer = {
      text: ''
    };
    const controller = new AbortController();
    abortRef.current = controller;
    const patchLast = fn => setMessages(prev => prev.map((m, i) => i === prev.length - 1 ? fn(m) : m));
    try {
      const token = await getToken();
      if (!token) throw new StreamError('Your session expired. Sign in again.');
      const res = await livyFetch(config, token, {
        method: 'POST',
        body: JSON.stringify(_objectSpread(_objectSpread(_objectSpread({
          conversationId,
          surface,
          context
        }, resident ? {
          resident
        } : {}), {}, {
          // Which support cards this chat holds; the proxy reads their status itself, only for the caller's own.
          supportRequestIds: [...new Set(history.flatMap(m => {
            var _m$supportOffer;
            return (_m$supportOffer = m.supportOffer) !== null && _m$supportOffer !== void 0 && _m$supportOffer.requestId ? [m.supportOffer.requestId] : [];
          }))]
        }, channel ? {
          voice: true
        } : {}), {}, {
          // Advisory, so answers can name the company; the proxy scopes data by the token.
          accountProfile: {
            companyName: selectedCompanyName(),
            billingType: billingType === null || billingType === void 0 ? void 0 : billingType()
          },
          messages: toApiHistory(history)
        })),
        signal: controller.signal
      });
      if (!res.ok || !res.body) throw new StreamError(await refusal(res));
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const seen = {
        complete: false
      };
      for (;;) {
        const _await$reader$read = await reader.read(),
          done = _await$reader$read.done,
          value = _await$reader$read.value;
        if (done) break;
        buffer = drainSse(buffer + decoder.decode(value, {
          stream: true
        }), (event, data) => {
          const delta = toDelta(event, data);
          if (!delta) return;
          if (delta.done) seen.complete = true;
          if (delta.blockStart && answer.text) answer.text = answer.text.replace(/\s*$/, '\n\n');
          if (delta.text) answer.text += delta.text;
          if (delta.blockStart) {
            patchLast(m => m.content ? _objectSpread(_objectSpread({}, m), {}, {
              content: m.content.replace(/\s*$/, '\n\n')
            }) : m);
          }
          if (delta.text) patchLast(m => _objectSpread(_objectSpread({}, m), {}, {
            content: m.content + delta.text
          }));
          if (delta.offer) patchLast(m => _objectSpread(_objectSpread({}, m), {}, {
            offer: delta.offer
          }));
          if (delta.emailOffer) patchLast(m => _objectSpread(_objectSpread({}, m), {}, {
            emailOffer: delta.emailOffer
          }));
          if (delta.supportOffer) patchLast(m => _objectSpread(_objectSpread({}, m), {}, {
            supportOffer: delta.supportOffer
          }));
          if (delta.shadowAction) {
            patchLast(m => {
              var _m$shadowActions;
              return _objectSpread(_objectSpread({}, m), {}, {
                shadowActions: [...((_m$shadowActions = m.shadowActions) !== null && _m$shadowActions !== void 0 ? _m$shadowActions : []), delta.shadowAction]
              });
            });
          }
          if (delta.exportArgs) patchLast(m => _objectSpread(_objectSpread({}, m), {}, {
            exportArgs: delta.exportArgs
          }));
          if (delta.live) setLive(delta.live);
          if (delta.toolName) {
            patchLast(m => {
              var _m$tools;
              return _objectSpread(_objectSpread({}, m), {}, {
                tools: [...((_m$tools = m.tools) !== null && _m$tools !== void 0 ? _m$tools : []), delta.toolName]
              });
            });
          }
          if (delta.requestId) {
            patchLast(m => {
              var _m$requestIds;
              return _objectSpread(_objectSpread({}, m), {}, {
                requestIds: [...new Set([...((_m$requestIds = m.requestIds) !== null && _m$requestIds !== void 0 ? _m$requestIds : []), delta.requestId])]
              });
            });
          }
        });
      }
      // A stream cut short by the server or something in between ends without error, so the
      // partial answer would otherwise be presented as finished and offered for copy, retry and
      // rating. Only message_stop means the answer is whole.
      if (!seen.complete) throw new StreamError('That answer was cut off. Try again.');
      // A person holds the chat: Livy said nothing, so the empty answer slot goes.
      setMessages(prev => {
        var _last$tools;
        const last = prev[prev.length - 1];
        return (last === null || last === void 0 ? void 0 : last.role) === 'assistant' && !last.content && !((_last$tools = last.tools) !== null && _last$tools !== void 0 && _last$tools.length) ? prev.slice(0, -1) : prev;
      });
      return answer.text;
    } catch (e) {
      // An aborted turn is the user pressing stop, not a failure.
      if ((e === null || e === void 0 ? void 0 : e.name) !== 'AbortError') {
        // A generic failure here is unactionable without the cause: the transport can throw from
        // the credential exchange or from signing, and neither reaches the user-facing message.
        if (!(e instanceof StreamError)) console.error('[Livy] turn failed', e);
        setError(e instanceof StreamError ? e.message : 'Something went wrong.');
      }
      return null;
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }, [config, getToken, context, surface, billingType, resident, conversationId, messages, streaming]);

  // While a person is asked for or in the chat, their lines (and the join / leave events) arrive by polling the
  // conversation; each carries the server's key, so nothing shows twice. Stops once the handoff ends.
  const liveActive = isLiveActive(live);
  useEffect(() => {
    if (!liveActive) return undefined;
    let stopped = false;
    let failures = 0;
    const poll = async () => {
      try {
        const res = await livyJson("/history/".concat(encodeURIComponent(conversationId)));
        if (stopped) return;
        setMessages(prev => {
          var _res$messages;
          const have = new Set(prev.map(m => m.id).filter(Boolean));
          // Only the handoff's own lines carry an id: rep lines, events, and Livy's "nobody could join" note.
          const fresh = ((_res$messages = res.messages) !== null && _res$messages !== void 0 ? _res$messages : []).filter(m => m.id && !have.has(m.id) && m.role !== 'user');
          return fresh.length ? [...prev, ...fresh] : prev;
        });
        // No live row any more means the handoff is over; the panel stops polling.
        setLive(prev => {
          var _res$live;
          return (_res$live = res.live) !== null && _res$live !== void 0 ? _res$live : prev ? _objectSpread(_objectSpread({}, prev), {}, {
            state: 'ended'
          }) : null;
        });
        failures = 0;
      } catch (_unused) {
        // A missed poll is retried; a chat that keeps refusing (gone, or not this person's) stops being polled.
        failures += 1;
        if (failures >= 5) setLive(prev => prev ? _objectSpread(_objectSpread({}, prev), {}, {
          state: 'ended'
        }) : null);
      }
    };
    void poll();
    const timer = setInterval(poll, LIVE_POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [liveActive, conversationId]);
  const reset = useCallback(() => {
    setLive(null);
    setMessages([]);
    setConversationId(crypto.randomUUID());
  }, []);

  /**
   * Reopens a past conversation from the transcript tier. The turn rows are dropped: they are
   * operational metadata, and replaying them as messages would put latency numbers in the thread.
   */
  const resume = useCallback(function (id, history) {
    let liveState = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
    setLive(liveState);
    // A reopened chat whose handoff is still on picks the banner and the poll back up. /history is the caller's own
    // conversation only; a chat that is not theirs (staff reading another's) simply has no live state here.
    if (!liveState && !id.startsWith('replay_')) {
      livyJson("/history/".concat(encodeURIComponent(id))).then(res => {
        var _res$live2;
        if (isLiveActive(res === null || res === void 0 ? void 0 : res.live)) setLive((_res$live2 = res.live) !== null && _res$live2 !== void 0 ? _res$live2 : null);
      }).catch(() => undefined);
    }
    setConversationId(id);
    setMessages(history);
    setError(null);
  }, []);

  /**
   * Re-asks the last question. The failed or unwanted answer is dropped first, so a retry replaces
   * it rather than stacking a second attempt underneath the first.
   */
  const retry = useCallback(() => {
    if (streaming) return;
    const lastUser = [...messages].reverse().find(m => m.role === 'user');
    if (!lastUser) return;
    const upTo = messages.lastIndexOf(lastUser);
    const truncated = messages.slice(0, upTo);
    setMessages(truncated);
    void send(lastUser.content, truncated);
  }, [messages, send, streaming]);
  return {
    messages,
    streaming,
    error,
    send,
    stop,
    reset,
    retry,
    resume,
    conversationId,
    live,
    startLive: setLive
  };
}